import React, { useState, useEffect } from 'react';
import { t, Trans } from '@lingui/macro';
import { Mail, User as UserIcon, MoreHorizontal, Link as LinkIcon, Copy, RefreshCw, Trash2, Check, Loader2, ChevronUp, ChevronDown } from 'lucide-react';
import { Workspace, WorkspaceMember } from '../../../models/Workspace';
import { Fire } from '../../../services/FireClient';
import { Util } from '../../../services/Utils/Util';
import { useToast } from '../../../components/ui/use-toast';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../../../components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../../components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "../../../components/ui/alert-dialog";
import { UUID } from '../../../models/local/Uid';
import { Clipboard } from "@capacitor/clipboard";
import { getMemberStorageStats, MemberStorageStat, formatBytes, updateAllocation } from '../../../services/StorageQuotaService';
import { UtilApp } from '../../../services/Utils/UtilApp';

interface WSAdminMembersProps {
    workspace: Workspace;
}

const WSAdminMembers: React.FC<WSAdminMembersProps> = ({ workspace }) => {
    const [members, setMembers] = useState<(WorkspaceMember & Partial<MemberStorageStat> & { lastActiveAt?: Date })[]>([]);
    const [inProgress, setInProgress] = useState(false);
    const { toast } = useToast();
    const [invitationDialogOpen, setInvitationDialogOpen] = useState(false);

    useEffect(() => {
        loadMembers();
    }, [workspace.id]);

    const loadMembers = async () => {
        setInProgress(true);
        try {
            const [list, stats] = await Promise.all([
                Fire.ws.getWorkspaceMembers(workspace.id),
                getMemberStorageStats(workspace.id)
            ]);

            const statsMap = new Map(stats.members.map(s => [s.userId, s]));
            const mergedList = list.map(m => ({
                ...m,
                allocatedBytes: statsMap.get(m.id)?.allocatedBytes ?? 0,
                usedBytes: statsMap.get(m.id)?.usedBytes ?? 0
            }));

            setMembers(mergedList);
        } catch (e) {
            console.error(e);
            toast({ title: t`Failed to load members` });
        } finally {
            setInProgress(false);
        }
    };

    return (
        <div className="w-full max-w-7xl p-2 sm:p-4 md:p-6 lg:p-8 space-y-6 bg-white my-2 sm:my-6 mx-0 sm:mx-4 rounded-none sm:rounded-panel shadow-none sm:shadow-sm border-0 sm:border">
            {/* Header - 모바일에서는 간소화 */}
            <div className="flex items-start justify-between gap-2 sm:gap-4 px-2 sm:px-0">
                <div className="flex items-center gap-2 sm:gap-4">
                    <div className="hidden sm:block p-3 rounded-xl bg-slate-100 border border-slate-200">
                        <UserIcon className="h-6 w-6 text-slate-600" />
                    </div>
                    <div>
                        <h2 className="text-lg sm:text-2xl font-bold tracking-tight"><Trans>Members</Trans></h2>
                        <p className="hidden sm:block text-sm text-muted-foreground"><Trans>Manage who has access to this workspace.</Trans></p>
                    </div>
                </div>
                <InvitationDialog workspace={workspace} open={invitationDialogOpen} onOpenChange={setInvitationDialogOpen} />
            </div>

            {/* Member List - 모바일에서는 구분선만, 데스크탑에서는 Card */}
            <div className="sm:hidden">
                {/* 모바일 레이아웃 */}
                <div className="text-sm font-medium text-muted-foreground px-2 pb-2 border-b">
                    <Trans>Member List</Trans> ({members.length})
                </div>
                {inProgress ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        <span className="ml-2 text-sm text-muted-foreground"><Trans>Loading...</Trans></span>
                    </div>
                ) : members.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                        <Trans>No members found</Trans>
                    </div>
                ) : (
                    <div className="divide-y">
                        {members.map((member) => (
                            <div key={member.id} className="flex items-center gap-3 py-3 px-2">
                                <Avatar className="h-9 w-9 shrink-0">
                                    <AvatarImage src={member.user.photoUrl || undefined} alt={member.user.name} />
                                    <AvatarFallback><UserIcon className="h-4 w-4" /></AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm truncate">{member.user.name}</div>
                                    <div className="text-xs text-muted-foreground truncate">{member.user.email}</div>
                                </div>
                                <div className="text-right text-[10px] text-muted-foreground shrink-0">
                                    <div>{(member as any).lastActiveAt ? UtilApp.dateStrToYmd((member as any).lastActiveAt) : '-'}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* 데스크탑 레이아웃 */}
            <Card className="hidden sm:block">
                <CardHeader>
                    <CardTitle><Trans>Member List</Trans> ({members.length})</CardTitle>
                    <CardDescription><Trans>List of all users currently in this workspace.</Trans></CardDescription>
                </CardHeader>
                <CardContent>
                    {inProgress ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                            <span className="ml-3 text-muted-foreground"><Trans>Loading members...</Trans></span>
                        </div>
                    ) : members.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <Trans>No members found</Trans>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {members.map((member) => (
                                <div key={member.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50/50 transition-colors">
                                    <Avatar className="h-10 w-10 shrink-0">
                                        <AvatarImage src={member.user.photoUrl || undefined} alt={member.user.name} />
                                        <AvatarFallback><UserIcon className="h-5 w-5" /></AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-sm truncate">{member.user.name}</div>
                                        <div className="text-xs text-muted-foreground truncate">{member.user.email}</div>
                                    </div>
                                    <div className="text-right text-xs text-muted-foreground shrink-0 whitespace-nowrap">
                                        <div><Trans>Last Active</Trans></div>
                                        <div>{(member as any).lastActiveAt ? UtilApp.dateStrToYmd((member as any).lastActiveAt) : '-'}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div >
    );
};

// Invitation Component
const InvitationDialog: React.FC<{
    workspace: Workspace;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}> = ({ workspace: initialWorkspace, open, onOpenChange }) => {
    // Use local state or refetch? workspace object might be stale if updated in parent?
    // Parent passes 'workspace', but if we update it here, we should be careful.
    // For now assuming we mutate the prop object (as seeing in other code) or better reload it.

    // Actually, in the original code, it mutated the workspace object.
    const [workspace, setWorkspace] = useState(initialWorkspace);
    const [key, setKey] = useState(initialWorkspace.invitationKey || "");
    const { toast } = useToast();
    const [loading, setLoading] = useState(false);
    const [isShowUrl, setIsShowUrl] = useState(false);

    useEffect(() => {
        setKey(initialWorkspace.invitationKey || "");
        setWorkspace(initialWorkspace);
    }, [initialWorkspace, open]);

    const getFullURL = (k: string) => {
        if (!k) return '';
        return `${Util.getAppUrl()}/ws-invite/${k}`;
    };

    const updateLink = async (newKey: string) => {
        setLoading(true);
        try {
            if (workspace) {
                workspace.invitationKey = newKey;
                await Fire.ws.updateWorkspace(workspace);
                setKey(newKey);
                toast({ title: t`Invitation link updated` });
            }
        } catch (e) {
            toast({ title: t`Failed to update link` });
        } finally {
            setLoading(false);
        }
    };

    const createNewLink = () => {
        const newKey = `${workspace.id}-${UUID.get()}`;
        updateLink(newKey);
    };

    const deleteLink = () => {
        updateLink("");
    };

    const copyToClipboard = async () => {
        const url = getFullURL(key);
        if (!url) return;
        await Clipboard.write({ string: url });
        toast({ title: t`Copied to clipboard` });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>
                <Button>
                    <LinkIcon className="mr-2 h-4 w-4" /> <Trans>Invite Member</Trans>
                </Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] sm:max-w-lg p-0 overflow-hidden">
                <div className="p-6 min-w-0">
                    <DialogHeader>
                        <DialogTitle><Trans>Invite Members</Trans></DialogTitle>
                        <DialogDescription>
                            <Trans>Anyone with this link can join your workspace.</Trans>
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6 py-4 min-w-0 overflow-hidden">
                        {key ? (
                            <div className="space-y-4 min-w-0">
                                <div className="space-y-2 min-w-0">
                                    <Label><Trans>Invitation Link</Trans></Label>
                                    <div
                                        onClick={() => setIsShowUrl(!isShowUrl)}
                                        className="group bg-orange-100/30 p-3 rounded-md text-xs sm:text-sm text-gray-600 cursor-pointer border border-orange-200 hover:border-orange-300 transition-all w-full overflow-hidden flex items-center justify-between gap-2 min-w-0"
                                    >
                                        <span className={`flex-1 min-w-0 ${!isShowUrl ? 'truncate block' : 'break-all whitespace-normal'}`}>
                                            {getFullURL(key)}
                                        </span>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <div className="text-gray-400 group-hover:text-orange-500 transition-colors">
                                                {isShowUrl ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 px-2 gap-1 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-200/50"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    copyToClipboard();
                                                }}
                                            >
                                                <Copy className="w-3.5 h-3.5" />
                                                <Trans>Copy</Trans>
                                            </Button>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-2">
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                            <Button variant="outline" className="w-full justify-start" disabled={loading}>
                                                <RefreshCw className="mr-2 h-4 w-4 shrink-0" /> <Trans>Regenerate Link</Trans>
                                            </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle><Trans>Create new invitation link?</Trans></AlertDialogTitle>
                                                <AlertDialogDescription>
                                                    <Trans>This will invalidate the current invitation link. Users attempting to join with the old link will get an error.</Trans>
                                                </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
                                                <AlertDialogAction onClick={createNewLink}><Trans>Regenerate</Trans></AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>

                                    <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                            <Button variant="outline" className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50" disabled={loading}>
                                                <Trash2 className="mr-2 h-4 w-4 shrink-0" /> <Trans>Delete Link</Trans>
                                            </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle><Trans>Delete invitation link?</Trans></AlertDialogTitle>
                                                <AlertDialogDescription>
                                                    <Trans>This will permanently remove the invitation link. No one will be able to join via link until you create a new one.</Trans>
                                                </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
                                                <AlertDialogAction onClick={deleteLink} className="bg-red-600 hover:bg-red-700"><Trans>Delete</Trans></AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-6">
                                <p className="text-muted-foreground mb-4"><Trans>No active invitation link.</Trans></p>
                                <Button onClick={createNewLink} disabled={loading}>
                                    <Trans>Create Link</Trans>
                                </Button>
                            </div>
                        )}
                    </div>

                    <DialogFooter className="sm:justify-start">
                        <div className="text-xs text-muted-foreground">
                            <Trans>Note: You can restrict allowed email domains in Settings.</Trans>
                        </div>
                    </DialogFooter>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default WSAdminMembers;
